import { useState } from 'react'
import { Trash, X, Loader2, ChevronDown, FolderInput } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDocumentsStore } from '@/stores/documents-store'
import { BID_DOCUMENT_CATEGORY_LABELS, type DocumentCategory } from '@/lib/schemas'
import { BID_MOVE_TO_CATEGORIES } from '@/lib/bid-document-groups'
import { toastApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

interface BidDocumentsSelectionBarProps {
  /** Hide bulk actions for viewers. The bar still shows the count + clear. */
  readOnly?: boolean
}

/**
 * Floating snackbar that appears when documents are selected.
 * Matches the Library DocumentPanel selection bar visually.
 */
export function BidDocumentsSelectionBar({
  readOnly = false,
}: BidDocumentsSelectionBarProps) {
  const selectedDocumentIds = useDocumentsStore((s) => s.selectedDocumentIds)
  const clearSelection = useDocumentsStore((s) => s.clearSelection)
  const bulkDelete = useDocumentsStore((s) => s.bulkDelete)
  const bulkCategorize = useDocumentsStore((s) => s.bulkCategorize)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isMoving, setIsMoving] = useState(false)

  if (selectedDocumentIds.size === 0) return null

  const handleConfirmDelete = async () => {
    setIsDeleting(true)
    try {
      await bulkDelete(Array.from(selectedDocumentIds))
      setConfirmOpen(false)
    } catch {
      // Error is surfaced via store; keep dialog open so the user can retry.
    } finally {
      setIsDeleting(false)
    }
  }

  // All-or-nothing: the server refuses the whole batch (an addendum in it, a
  // missing document) and the store rolls every row back. The selection stays
  // so the user can adjust it and try again.
  const handleMove = async (category: DocumentCategory) => {
    setIsMoving(true)
    try {
      await bulkCategorize(Array.from(selectedDocumentIds), category)
      clearSelection()
    } catch (error) {
      toastApiError(error, 'Failed to move documents')
    } finally {
      setIsMoving(false)
    }
  }

  const count = selectedDocumentIds.size

  return (
    <>
      <div
        data-testid="bid-documents-selection-bar"
        className={cn(
          'absolute bottom-4 left-1/2 -translate-x-1/2 z-40',
          'flex items-center gap-2 px-3 py-2',
          'rounded-lg shadow-md border border-foreground/10',
          'bg-foreground text-background',
          'animate-in slide-in-from-bottom-2 fade-in duration-150'
        )}
        role="status"
        aria-live="polite"
      >
        <span className="text-sm font-medium px-1 tabular-nums">
          {count} selected
        </span>
        {!readOnly && (
          <>
            <div className="h-4 w-px bg-background/20 mx-1" aria-hidden />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background"
                  disabled={isMoving}
                  data-testid="bid-documents-bulk-move"
                >
                  {isMoving ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <FolderInput className="h-4 w-4 mr-1.5" />
                  )}
                  Move to
                  <ChevronDown className="h-3.5 w-3.5 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" className="w-56">
                {BID_MOVE_TO_CATEGORIES.map((category) => (
                  <DropdownMenuItem key={category} onClick={() => void handleMove(category)}>
                    {BID_DOCUMENT_CATEGORY_LABELS[category]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2.5 text-background hover:bg-background/10 hover:text-background"
              onClick={() => setConfirmOpen(true)}
              data-testid="bid-documents-bulk-delete"
            >
              <Trash className="h-4 w-4 mr-1.5" />
              Delete
            </Button>
          </>
        )}
        <div className="h-4 w-px bg-background/20 mx-1" aria-hidden />
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-background hover:bg-background/10 hover:text-background"
          onClick={clearSelection}
          aria-label="Clear selection"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => !isDeleting && setConfirmOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {count} {count === 1 ? 'document' : 'documents'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The selected documents will be
              permanently removed from this bid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
